import React, { Fragment } from "react";
import { useTheme, Box, Typography } from '@mui/material';
import { ResponsiveBar } from "@nivo/bar";
import { computeXYScalesForSeries } from "@nivo/scales";
import { useTooltip, TableTooltip } from "@nivo/tooltip";
import { Axes } from "@nivo/axes";
import { line } from "d3-shape";

const BarLineChart = ({ 
  data = [], 
  title = "",
  bottomLegend = "X Axis", 
  leftLegend = "Y Axis", 
  isLinesVisible = true, 
  isHorizontalBottomLegend,
  digit = 1,
  topOffSet = 0,
  bottomOffSet = 0,
  bottomLegendOffSet = 0, 
}) => {  
  const theme = useTheme();
  
  if (!data || data.length === 0 || !Array.isArray(data)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }

  const formattedData = data
    .map(e => {
      return Object.fromEntries(
        Object.entries(e).map(([key, value]) => [
          key,
          typeof value === "number" ? Number(value.toFixed(digit)) : value
        ])
      );
    })
    .filter(e => !Object.values(e).includes(0)); // remove if any value === 0



  const barColor = 'hsl(200, 70%, 50%)';
  const lineColor = 'hsl(120, 70%, 50%)';

  const CustomLine = ({ bars, innerWidth, innerHeight }) => {    
    const scale = computeXYScalesForSeries(
      [{ 
        id: "only", 
        data: bars.map(bar => ({ x: bar.data.index, y: bar.data.data.z })) 
      }],
      { type: "linear" },
      { type: "linear" },
      innerWidth,
      innerHeight
    );
    
    const lineGenerator = line()
      .x((bar) => bar.x + bar.width / 2)
      .y((bar) => scale.yScale(bar.data.data.z));

    const tooltip  = useTooltip();
    function renderTip(e, idx) {
      return tooltip.showTooltipFromEvent(
        <CustomTooltip 
          barValue={formattedData[idx].y}  
          barUnit='flights' 
          lineValue={formattedData[idx].z} 
          lineUnit='hours' 
        />,
        e
      );
    }
    
    return (
      <Fragment>
        <Axes
          yScale={scale.yScale}
          xScale={scale.xScale}
          width={innerWidth}
          height={innerHeight}
          right={{
            ticksPosition: "after"
          }}
        />
        <path
          d={lineGenerator(bars)}
          fill="none"
          stroke={lineColor}
          strokeWidth={2}
          style={{ pointerEvents: "none" }}
        />        
        {bars?.map((bar) => (          
          <Fragment key={bar.key}>
            <circle
              key={bar.key}
              cx={bar.x + bar.width / 2}
              cy={scale.yScale(bar?.data?.data?.z)}
              r={4}
              fill="white"
              stroke={lineColor}
              style={{ pointerEvents: "none" }}
            />
            <text
              x={bar.x + bar.width / 2}
              y={scale.yScale(bar.data.data.z) - 15} 
              fill={lineColor}
              fontSize={12}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {bar.data.data.z} {/* Format the label text here */}
            </text>
          </Fragment>
        ))}
        {bars.map((bar, idx) => (
          <rect
            key={bar.key}
            x={bar.x}
            y={0}
            height={innerHeight}
            width={bar.width}
            fill="transparent"
            onMouseEnter={(e) => renderTip(e, idx)}
            onMouseMove={(e) => renderTip(e, idx)}
            onMouseLeave={tooltip.hideTooltip}
          />
        ))}
      </Fragment>
    );
  };

  function CustomTooltip({ barValue, barUnit, lineValue, lineUnit }) {
    return (
      <TableTooltip
        rows={[
          ["Line:", lineValue, lineUnit],
          ["Bar:", barValue, barUnit],          
        ]}
      />
    );
  }

  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      gap={1} 
      sx={{ height: '100%', width: '100%' }}
    >
      {title && 
        <Typography 
          variant="h6" 
          align="center"
        >
          {title}
        </Typography>
      }
      
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        <ResponsiveBar
          data={formattedData}
          indexBy="x"
          keys={["y"]}
          margin={{
            top: 25 + (topOffSet || 0),
            right: 44,
            bottom: 185 + (bottomOffSet || 0), 
            left: 80
          }}
          padding={0.4}
          enableLabel={true}
          labelTextColor={theme.palette.text.primary}
          totalsTextColor={theme.palette.text.primary}
          colors={[barColor]}
          borderRadius={2}
          axisBottom={{
            tickSize: 5,
            tickPadding: 45 + bottomLegendOffSet,
            tickRotation: isHorizontalBottomLegend ? 0 : 270,
            legend: bottomLegend,
            legendPosition: 'middle',
            legendOffset: 75 + bottomLegendOffSet,
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: leftLegend || 'Value',
            legendPosition: 'middle',
            legendOffset: -60,
          }}
          enableGridY={false}
          layers={[
            'grid', 'axes', 'bars', 'markers', 'legends', 
            isLinesVisible && ((props) => <CustomLine {...props} />)
          ].filter(Boolean)}
          legends={[
            {
              data: [
                {
                  label: "Flights", 
                  color: barColor,
                }
              ], 
              anchor: 'top-right',
              direction: 'row',
              justify: false,
              translateX: -60,
              translateY: -50,
              itemWidth: 75,
              itemHeight: 20,
              itemsSpacing: 2,
              symbolSize: 12,
              symbolShape: 'circle',
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
            {
              data: [
                {
                  label: "Block Hours", 
                  color: lineColor,
                }
              ],
              anchor: 'top-right',
              direction: 'row',
              justify: false,
              translateX: 15,
              translateY: -50,
              itemWidth: 75,
              itemHeight: 20,
              symbolSize: 12,
              symbolShape: 'circle',
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemBackground: 'rgba(0, 0, 0, .03)',
                    itemOpacity: 1,
                  },
                },
              ],
            },
          ]}
          theme={{
            axis: {
              domain: {
                line: {
                  stroke: theme.palette.neutral.dark,
                },
              },
              legend: {
                text: {
                  fill: theme.palette.text.primary,
                },
              },
              ticks: {
                line: {
                  stroke: theme.palette.neutral.dark,
                  strokeWidth: 1,
                },
                text: {
                  fill: theme.palette.text.primary,
                },
              },
            },  
            legends: {
              text: {
                fill: theme.palette.text.primary,
              },
            },         
            grid: {
              line: {
                stroke: theme.palette.neutral.dark,
                strokeWidth: 0.2,
                strokeDasharray: '2 2'
              }
            },
            tooltip: {
              container: {
                background: theme.palette.neutral.light,
                color: theme.palette.text.primary,
                borderRadius: '4px',
                padding: '8px',
              },
            },
            labels: {
              text: {
                fill: theme.palette.text.primary,
                fontSize: '12px' 
              },
            },
            dots: {
              text: {
                fill: theme.palette.text.primary,
              },
            },
          }} 
        />
      </Box>
    </Box>
  );
};

export default BarLineChart;
